/**
 * Cliente de autenticación (Sprint 16 — cookie httpOnly + sesión server-side).
 *
 * El token YA NO existe en el cliente: viaja en una cookie httpOnly que el
 * navegador adjunta solo (fetch con credentials:'include'). JavaScript no
 * puede leerla → XSS no puede exfiltrar la sesión. La identidad se consulta
 * vía GET /me y se cachea EN MEMORIA (módulo singleton):
 *
 *   - Tras un reload la memoria está vacía → AuthGuard hace un probe async a
 *     /me antes de renderizar rutas protegidas.
 *   - setCurrentUser notifica a los suscriptores (useSyncExternalStore en
 *     UserBadge) para que la UI reaccione a login/logout sin prop-drilling.
 *
 * No queda NINGÚN uso de localStorage/sessionStorage para auth: al cargar el
 * módulo purgamos los restos de la era Sprint 8 (jr2mkt.authToken). Los JWT
 * antiguos ya no valen de todas formas: el backend rechaza cualquier header
 * Authorization con 401 (corte limpio, sin revocación no eran aceptables).
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'

export interface CurrentUser {
  id: string
  email: string
  name: string | null
  role: string
  mustChangePassword: boolean
}

// Higiene: purga de los restos del auth Sprint 8. Best-effort (Safari privado).
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('jr2mkt.authToken')
    window.sessionStorage.removeItem('jr2mkt.currentUser')
  } catch {
    // noop
  }
}

let currentUser: CurrentUser | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/** Suscripción a cambios del usuario en memoria (para useSyncExternalStore). */
export function subscribeCurrentUser(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getCurrentUser(): CurrentUser | null {
  return currentUser
}

export function setCurrentUser(user: CurrentUser | null): void {
  currentUser = user
  notify()
}

/**
 * Probe de identidad: GET /me con la cookie.
 *
 * Contrato (Sprint 19 — F6):
 *   - 200      → cachea y devuelve el user.
 *   - 401/403  → sesión inválida/anónima: limpia cache y devuelve null.
 *                Es el ÚNICO caso que significa "no autenticado".
 *   - red/5xx  → LANZA (Error). No toca el cache: un blip de red o un server
 *                caído NO debe "desloguear" al usuario ni expulsarlo a /login.
 *                El caller decide (AuthGuard muestra pantalla de reintento;
 *                LoginPage/ChangePasswordPage lo tratan como "no sé").
 */
export async function fetchMe(): Promise<CurrentUser | null> {
  const res = await fetch(`${BASE}/v1/marketing/auth/me`, { credentials: 'include' })
  if (res.status === 401 || res.status === 403) {
    setCurrentUser(null)
    return null
  }
  if (!res.ok) {
    throw new Error(`No se pudo comprobar la sesión (HTTP ${res.status})`)
  }
  const user = (await res.json()) as CurrentUser
  setCurrentUser(user)
  return user
}

export type LoginResult =
  | { ok: true; user: CurrentUser }
  | { ok: false; message: string }

/**
 * Login: el backend crea la sesión server-side y responde Set-Cookie httpOnly
 * + { user } (sin token en el body). fetch directo — no api() — porque el 401
 * debe mostrarse en el formulario, no redirigir.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  const res = await fetch(`${BASE}/v1/marketing/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const data = (await res.json().catch(() => ({}))) as {
    user?: CurrentUser
    error?: string
    message?: string
  }
  if (!res.ok) {
    return { ok: false, message: data.message ?? 'credenciales inválidas' }
  }
  if (!data.user) {
    return { ok: false, message: 'respuesta inesperada del servidor' }
  }
  setCurrentUser(data.user)
  return { ok: true, user: data.user }
}

/**
 * Logout: revoca la sesión server-side y limpia la cookie (Set-Cookie de
 * borrado emitido por el backend).
 *
 * Best-effort GARANTIZADO (Sprint 19 — F8): ante respuesta no-ok o fallo de
 * red se limpia IGUALMENTE el estado local — el cliente nunca se queda en
 * limbo. Caveat documentado: si el server no respondió, la cookie httpOnly
 * puede quedar viva hasta caducar por idle (30 min) o por cap absoluto (8 h);
 * es aceptable porque el cache local ya está vacío y el siguiente 401 de
 * api.ts redirige a /login. Nunca lanza. La navegación a /login la hace el
 * caller SIEMPRE, con o sin éxito del POST (UserBadge navega tras el await).
 */
export async function logout(): Promise<void> {
  try {
    // Una respuesta no-ok (5xx, 403 de origin…) NO se trata distinta del
    // éxito a efectos de cliente: la revocación server-side pudo no ocurrir,
    // pero localmente cortamos igual (ver doc-comment).
    await fetch(`${BASE}/v1/marketing/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch {
    // Fallo de red: el server-side caducará por idle; localmente cortamos.
  } finally {
    setCurrentUser(null)
  }
}
